import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Mail, User, ShieldCheck, Lock, Handshake } from "lucide-react";

const LOGO_URL = "https://customer-assets-gfyr7b9c.emergentagent.net/job_bookstore-hub-112/artifacts/qc0wmxlg_file_00000000a5c0820e812c666fe101a95b.png";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthorizePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lastAccount, setLastAccount] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // If already logged in, skip the consent screen
    if (user) {
      navigate("/bibliotheque", { replace: true });
      return;
    }
    const cached = localStorage.getItem("bb_last_google");
    if (cached) {
      try { setLastAccount(JSON.parse(cached)); } catch { /* ignore */ }
    }
  }, [user, navigate]);

  const authorize = () => {
    setLoading(true);
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const refuse = () => {
    navigate("/login", { replace: true });
  };

  const switchAccount = () => {
    localStorage.removeItem("bb_last_google");
    setLastAccount(null);
  };

  const initial = lastAccount?.email ? lastAccount.email.charAt(0).toUpperCase() : "";

  return (
    <div className="min-h-screen w-full bg-[#0A0A0B] text-white flex flex-col items-center px-5 py-8 sm:py-14">
      {/* Header logo + brand */}
      <div className="flex flex-col items-center text-center max-w-md w-full">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="BARON BOOKS" className="w-14 h-14 rounded-xl" />
        </div>
        <h1 className="mt-4 font-serif text-3xl sm:text-4xl tracking-[0.15em] font-medium">
          BARON <span className="text-[#D4A65A]">BOOKS</span>
        </h1>
        <div className="mt-2 h-px w-32 bg-[#D4A65A]/40" />
        <p className="mt-3 text-[11px] sm:text-xs tracking-[0.28em] uppercase text-neutral-400">
          Des histoires qui éveillent,<br className="sm:hidden" />
          <span className="hidden sm:inline"> </span>inspirent et transforment.
        </p>
      </div>

      {/* Consent card */}
      <div className="mt-8 w-full max-w-md rounded-2xl border border-[#D4A65A]/15 bg-[#0F0F11]/90 backdrop-blur-sm px-6 sm:px-8 py-8 shadow-2xl">
        {/* Icon tile */}
        <div className="flex justify-center">
          <div className="w-20 h-20 rounded-2xl border border-[#D4A65A]/25 bg-black flex items-center justify-center">
            <img src={LOGO_URL} alt="" className="w-14 h-14 rounded-lg" />
          </div>
        </div>

        {/* Title */}
        <h2 className="mt-6 text-center font-serif text-2xl sm:text-[28px] leading-tight">
          Autoriser <span className="text-[#D4A65A]">Baron Books</span><br />
          à accéder à votre compte
        </h2>

        <p className="mt-4 text-center text-sm text-neutral-400">
          Cela permettra à Baron Books de voir :
        </p>

        {/* Permissions */}
        <ul className="mt-6 space-y-4">
          <li className="flex items-start gap-4">
            <div className="w-11 h-11 shrink-0 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center">
              <Mail className="w-5 h-5 text-neutral-300" />
            </div>
            <div>
              <div className="text-[15px] font-medium">Votre adresse e-mail</div>
              <div className="mt-0.5 text-[13px] text-neutral-400">Pour créer ou connecter votre compte</div>
            </div>
          </li>
          <li className="flex items-start gap-4">
            <div className="w-11 h-11 shrink-0 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center">
              <User className="w-5 h-5 text-neutral-300" />
            </div>
            <div>
              <div className="text-[15px] font-medium">Vos informations de profil</div>
              <div className="mt-0.5 text-[13px] text-neutral-400">Pour personnaliser votre expérience</div>
            </div>
          </li>
        </ul>

        {/* Actions */}
        <div className="mt-8 grid grid-cols-2 gap-3">
          <button
            type="button"
            data-testid="authorize-refuse-btn"
            onClick={refuse}
            className="h-12 rounded-xl bg-[#1E1E20] hover:bg-[#26262A] border border-white/10 text-[15px] font-medium transition-colors"
          >
            Refuser
          </button>
          <button
            type="button"
            data-testid="authorize-allow-btn"
            onClick={authorize}
            disabled={loading}
            className="h-12 rounded-xl bg-[#C08A3E] hover:bg-[#D19A4C] text-white text-[15px] font-semibold transition-colors disabled:opacity-60 shadow-[0_10px_24px_-8px_rgba(192,138,62,0.55)]"
          >
            {loading ? "Redirection..." : "Autoriser"}
          </button>
        </div>

        {/* Divider */}
        <div className="mt-8 h-px bg-white/5" />

        {/* Last account */}
        <div className="mt-6 text-center">
          <div className="text-xs text-neutral-500 tracking-wide">Connecté en tant que</div>
          <div className="mt-3 inline-flex items-center gap-3 px-2 py-1">
            <div className="w-9 h-9 rounded-full bg-[#3A2E1D] text-[#D4A65A] flex items-center justify-center text-sm font-medium border border-[#D4A65A]/30">
              {initial || "?"}
            </div>
            <div className="text-sm text-neutral-200">
              {lastAccount?.email || "Aucun compte enregistré"}
            </div>
          </div>
          <div className="mt-3">
            <button
              type="button"
              onClick={switchAccount}
              data-testid="authorize-switch-btn"
              className="text-[13px] text-neutral-400 hover:text-[#D4A65A] underline underline-offset-4 transition-colors"
            >
              Utiliser un autre compte
            </button>
          </div>
        </div>
      </div>

      {/* Trust footer */}
      <div className="mt-8 mb-4 grid grid-cols-3 gap-3 w-full max-w-md text-center text-[11px] text-neutral-500">
        <div className="flex flex-col items-center gap-1.5">
          <ShieldCheck className="w-4 h-4 text-neutral-400" />
          Connexion sécurisée
        </div>
        <div className="flex flex-col items-center gap-1.5">
          <Lock className="w-4 h-4 text-neutral-400" />
          Vos données sont protégées
        </div>
        <div className="flex flex-col items-center gap-1.5">
          <Handshake className="w-4 h-4 text-neutral-400" />
          Confiance & transparence
        </div>
      </div>
    </div>
  );
}
